/**
 * Crypto Type Definitions for Customer App
 *
 * These types define the encryption data structures used for
 * zero-knowledge E2E encryption between consultant and customer.
 */

/**
 * AES-256-GCM encrypted data structure
 *
 * Used for all encrypted data storage:
 * - Customer profile data
 * - Order details
 * - Appointment information
 */
export interface EncryptedData {
  /** Base64-encoded ciphertext (includes auth tag in GCM mode) */
  ciphertext: string
  /** Base64-encoded 12-byte initialization vector */
  iv: string
  /** Base64-encoded authentication tag (empty for GCM - included in ciphertext) */
  authTag: string
}

/**
 * ECIES-wrapped DEK structure
 *
 * Authenticated ECIES uses TWO shared secrets to prevent MITM attacks:
 * - ss1 = ECDH(ephemeral, customer) - Forward secrecy
 * - ss2 = ECDH(consultantStatic, customer) - Authentication
 *
 * The customer verifies consultant identity by computing ss2 using
 * the consultant's known static public key.
 */
export interface CustomerWrappedDEK {
  /** Base64-encoded ephemeral X25519 public key (for forward secrecy) */
  ephemeralPublicKey: string
  /** Base64-encoded consultant's static X25519 public key (for authentication) */
  consultantStaticPublicKey: string
  /** Base64-encoded AES-256-GCM encrypted DEK */
  encryptedDEK: string
  /** Base64-encoded 12-byte IV for AES-256-GCM */
  iv: string
}

/**
 * Key derivation result from passphrase
 */
export interface DerivedKey {
  /** 256-bit derived key */
  key: Uint8Array
  /** Salt used for derivation (must be stored for re-derivation) */
  salt: Uint8Array
}

/**
 * Customer X25519 key pair
 *
 * Generated from Argon2id-derived seed:
 * passphrase + salt → Argon2id → 32-byte seed → X25519 key pair
 */
export interface CustomerKeyPair {
  /** X25519 private key (32 bytes) */
  privateKey: Uint8Array
  /** X25519 public key (32 bytes) */
  publicKey: Uint8Array
}

/**
 * Key exchange status for customer registration
 */
export type KeyExchangeStatus = 'invited' | 'registered' | 'active' | 'revoked'

/**
 * Response from /auth/key-exchange-status endpoint
 */
export interface KeyExchangeStatusResponse {
  status: KeyExchangeStatus
  customerId: string
  consultantId: string
  /** Present only when status is 'active' */
  wrappedDEK?: CustomerWrappedDEK
}

/**
 * Response from /auth/dek endpoint
 */
export interface DEKResponse {
  /** ECIES-wrapped DEK */
  wrappedDEK: CustomerWrappedDEK
  /** DEK version (for rotation support) */
  version: number
}

/**
 * Passphrase strength validation result
 */
export interface PassphraseStrength {
  /** Whether passphrase meets minimum requirements */
  isValid: boolean
  /** Score from 0-100 */
  score: number
  /** Human-readable feedback messages */
  feedback: string[]
}

/**
 * Encryption context state
 */
export interface EncryptionState {
  /** Whether the encryption layer is unlocked (DEK available) */
  isUnlocked: boolean
  /** Whether the encryption layer is currently loading */
  isLoading: boolean
  /** Error message if unlock failed */
  error: string | null
  /** Current DEK version (for rotation support) */
  dekVersion: number | null
}

/**
 * Registration request body
 */
export interface CustomerRegistrationRequest {
  /** Invitation token from URL */
  invitationToken: string
  /** Customer's email */
  email: string
  /** Customer's X25519 public key (Base64) */
  publicKey: string
  /** Argon2id salt for key derivation (Base64) */
  keySalt: string
}

/**
 * Registration response
 */
export interface CustomerRegistrationResponse {
  customerId: string
  status: KeyExchangeStatus
  message: string
}

/**
 * Argon2id parameters for key derivation
 *
 * These parameters provide strong protection against brute-force attacks:
 * - Memory: 64 MB makes GPU attacks expensive
 * - Iterations: 3 passes for security/usability balance
 * - Parallelism: 4 threads for modern CPUs
 */
export const ARGON2_PARAMS = {
  /** Number of iterations (time cost) */
  t: 3,
  /** Memory usage in KB (64 MB) */
  m: 65536,
  /** Number of parallel threads */
  p: 4,
  /** Output length in bytes (256 bits) */
  dkLen: 32,
} as const

/**
 * AES-GCM algorithm constants
 */
export const AES_CONSTANTS = {
  ALGORITHM: 'AES-GCM',
  KEY_LENGTH: 256,
  IV_LENGTH: 12, // 96 bits (recommended for GCM)
} as const

/**
 * HKDF info string for ECIES key derivation
 */
export const HKDF_INFO = 'CustomerDEK-ECIES-v1'
